/** Matches snippets/func.format-dimension-number.liquid + market metafield custom.number_format_pattern. */

export type NumberFormatPattern = 'Comma-Period' | 'Period-Comma' | 'Space-Comma';

type NumberFormatSeparators = {
  thousands: string;
  decimal: string;
};

const DEFAULT_PATTERN: NumberFormatPattern = 'Comma-Period';

const PATTERN_VALUES: ReadonlyArray<NumberFormatPattern> = [
  'Comma-Period',
  'Period-Comma',
  'Space-Comma',
];

export const normalizeNumberFormatPattern = (
  raw: string | null | undefined,
): NumberFormatPattern => {
  const token = String(raw ?? '').trim();
  const match = PATTERN_VALUES.find((pattern) => pattern === token);
  return match ?? DEFAULT_PATTERN;
};

const digitRunLength = (value: string, fromIndex: number): number =>
  value.slice(fromIndex + 1).replace(/\D/g, '').length;

const integerDigitsBefore = (value: string, separatorIndex: number): number =>
  value.slice(0, separatorIndex).replace(/[^\d-]/g, '').length;

const looksLikeDecimalComma = (value: string, commaIndex: number): boolean => {
  const after = digitRunLength(value, commaIndex);
  if (after < 1 || after > 2) {
    return false;
  }

  const before = integerDigitsBefore(value, commaIndex);
  if (before === 0) {
    return false;
  }

  return after !== 3 || before <= 3;
};

const looksLikeThousandsPeriod = (value: string, periodIndex: number): boolean => {
  const after = digitRunLength(value, periodIndex);
  if (after !== 3) {
    return false;
  }

  return integerDigitsBefore(value, periodIndex) >= 1;
};

const normalizeSingleComma = (value: string, pattern: NumberFormatPattern): string => {
  const commaIndex = value.lastIndexOf(',');

  if (pattern === 'Period-Comma' || pattern === 'Space-Comma') {
    return value.replace(/\s/g, '').replace(',', '.');
  }

  if (looksLikeDecimalComma(value, commaIndex)) {
    const before = value.slice(0, commaIndex).replace(/[^\d-]/g, '');
    const after = value.slice(commaIndex + 1).replace(/\D/g, '');
    return `${before}.${after}`;
  }

  return value.replace(/,/g, '');
};

const normalizeSinglePeriod = (value: string, pattern: NumberFormatPattern): string => {
  if (pattern === 'Comma-Period') {
    return value;
  }

  const periodIndex = value.lastIndexOf('.');

  if (looksLikeThousandsPeriod(value, periodIndex)) {
    return value.replace(/\./g, '');
  }

  return value;
};

const normalizeBothSeparators = (value: string): string => {
  const lastComma = value.lastIndexOf(',');
  const lastPeriod = value.lastIndexOf('.');

  if (lastComma > lastPeriod) {
    return value.replace(/\./g, '').replace(',', '.');
  }

  return value.replace(/,/g, '');
};

const separatorsForPattern = (
  pattern: NumberFormatPattern = DEFAULT_PATTERN,
): NumberFormatSeparators => {
  if (pattern === 'Period-Comma') {
    return { thousands: '.', decimal: ',' };
  }

  if (pattern === 'Space-Comma') {
    return { thousands: ' ', decimal: ',' };
  }

  return { thousands: ',', decimal: '.' };
};

export const decimalPlacesFromHint = (
  raw: string,
  pattern: NumberFormatPattern = DEFAULT_PATTERN,
): number => {
  const lastComma = raw.lastIndexOf(',');
  const lastPeriod = raw.lastIndexOf('.');

  if (lastComma !== -1 && lastPeriod !== -1) {
    const decimalIndex = Math.max(lastComma, lastPeriod);
    return raw.slice(decimalIndex + 1).replace(/\D/g, '').length;
  }

  if (lastComma !== -1 && looksLikeDecimalComma(raw, lastComma)) {
    return digitRunLength(raw, lastComma);
  }

  if (lastPeriod !== -1) {
    if (pattern !== 'Comma-Period' && looksLikeThousandsPeriod(raw, lastPeriod)) {
      return 0;
    }

    const after = digitRunLength(raw, lastPeriod);
    if (after >= 1 && after <= 2) {
      return after;
    }
  }

  const { decimal } = separatorsForPattern(pattern);
  const index = raw.lastIndexOf(decimal);
  if (index === -1) {
    return 0;
  }

  return raw.slice(index + 1).replace(/\D/g, '').length;
};

export const parseMarketNumber = (
  raw: string,
  pattern: NumberFormatPattern = DEFAULT_PATTERN,
): number | null => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  const hasComma = trimmed.includes(',');
  const hasPeriod = trimmed.includes('.');

  let normalized = trimmed;

  if (hasComma && hasPeriod) {
    normalized = normalizeBothSeparators(trimmed);
  } else if (hasComma) {
    normalized = normalizeSingleComma(trimmed, pattern);
  } else if (hasPeriod) {
    normalized = normalizeSinglePeriod(trimmed, pattern);
  }

  const value = Number.parseFloat(normalized);
  return Number.isFinite(value) ? value : null;
};

const formatIntegerGroups = (integer: string, thousands: string): string => {
  if (integer.length <= 3) {
    return integer;
  }

  const parts: string[] = [];
  let head = integer;

  while (head.length > 3) {
    parts.unshift(head.slice(-3));
    head = head.slice(0, -3);
  }

  if (head) {
    parts.unshift(head);
  }

  return parts.join(thousands);
};

const truncateTo = (value: number, places: number): number => {
  if (places <= 0) {
    return Math.trunc(value);
  }

  const factor = 10 ** places;
  const scaled = value * factor;

  return Math.trunc(scaled + (scaled >= 0 ? 1e-9 : -1e-9)) / factor;
};

export const formatMarketNumber = (
  value: number,
  options: {
    pattern?: NumberFormatPattern;
    decimalPlaces?: number;
    sourceHint?: string;
  } = {},
): string => {
  if (!Number.isFinite(value)) {
    return '';
  }

  const pattern = options.pattern ?? DEFAULT_PATTERN;
  const { thousands, decimal } = separatorsForPattern(pattern);
  const hint = options.sourceHint ?? '';

  const places =
    options.decimalPlaces ??
    (hint ? decimalPlacesFromHint(hint, pattern) : Number.isInteger(value) ? 0 : 1);

  const displayPlaces = places > 0 ? places : Number.isInteger(value) ? 0 : 1;
  const truncated = truncateTo(value, displayPlaces);
  const [integerPart = '0', fractional = ''] = truncated.toFixed(displayPlaces).split('.');
  const integerFormatted = formatIntegerGroups(integerPart, thousands);

  if (displayPlaces <= 0) {
    return integerFormatted;
  }

  const decimalPart = fractional.replace(/0+$/, '');
  if (!decimalPart) {
    return integerFormatted;
  }

  return `${integerFormatted}${decimal}${decimalPart}`;
};
